import { useMemo } from "react";

import { useTranslation } from "next-i18next";

import { MedalOutline } from "@/assets/icons/24/MedalOutline";
import { ProfileOutlined } from "@/assets/icons/24/ProfileOutlined";
import { InfoLabel, InfoLabelProps } from "@/ui/cva/InfoLabel";
import { MerchantType } from "@whoppah/apollo/schema";
import { IdeaBulb } from "@whoppah/icons/24/IdeaBulb";

type LabelVariant = {
  color: InfoLabelProps["color"];
  icon: (props: { className: string }) => JSX.Element;
  labelLokaliseKey: string;
};

const LABEL_VARIANTS: Record<
  "brand" | "businessMerchant" | "default",
  LabelVariant
> = {
  brand: {
    color: "brand",
    icon: IdeaBulb,
    labelLokaliseKey: "pdp:pdp-label-this-is-yours",
  },
  businessMerchant: {
    color: "brand",
    icon: MedalOutline,
    labelLokaliseKey: "pdp:pdp-info-professional-seller-label",
  },
  default: {
    color: "attention",
    icon: ProfileOutlined,
    labelLokaliseKey: "pdp:pdp-info-private-seller",
  },
};

export const ProductDetailsSellerLabel = ({
  merchantType,
  sellerIsOwner,
  className,
}: {
  merchantType: MerchantType;
  className?: string;
  sellerIsOwner?: boolean;
}) => {
  const { t } = useTranslation();

  const variant = useMemo(() => {
    const isBusinessMerchant = merchantType === MerchantType.Business;

    if (sellerIsOwner) {
      return LABEL_VARIANTS.brand;
    }

    return isBusinessMerchant
      ? LABEL_VARIANTS.businessMerchant
      : LABEL_VARIANTS.default;
  }, [merchantType, sellerIsOwner]);

  return (
    <InfoLabel
      className={className}
      color={variant.color}
      rightIcon={<variant.icon className="h-4 w-4" />}
    >
      {t(variant.labelLokaliseKey)}
    </InfoLabel>
  );
};
